/**
 * Action warning text helpers
 *
 * Provides utilities for generating context-aware warning text
 * for significant actions (external impact / one-way doors)
 */

import {
  deriveEntityType,
  getEntityTypeLabel,
} from '@/components/proposals/shared/utils';
import { pluralizeEntityLabel } from '@/lib/crm/utils/entityTypeUtils';

import type {
  CalendarEventCreatePayload,
  GmailSendPayload,
  ObjectDeletedEntityPayload,
  ObjectMergedEntityPayload,
  PropertyUpdateEntityPayload,
  ProposalDetail,
  ProposedAction,
} from '../core/types';
import {
  isCalendarEventCreatePayload,
  isGmailSendPayload,
  isObjectDeletedPayload,
  isObjectMergedPayload,
  isPropertyUpdatePayload,
} from '../core/types';
import { lookupAction } from '../registry';
import { mergeActions, type MergedOrOriginal } from './actionMerging';

/**
 * Warning text result for a single action
 */
export interface ActionWarningText {
  /** Condensed text for inline display */
  condensed: string;
  /** Full text for tooltip display */
  tooltip: string;
}

/**
 * Multi-action summary result
 */
export interface MultiActionSummary {
  /** Summary text like "3 actions: email, delete, calendar" */
  summary: string;
  /** Tooltip items - one per significant action */
  tooltipItems: string[];
}

/**
 * Action type labels for display
 */
type ActionTypeLabel = 'email' | 'calendar' | 'delete' | 'merge';

/**
 * Check if an action is significant (requires warning)
 *
 * Significant actions are:
 * - gmail_send: Always (external impact)
 * - calendar_event_create: Only if attendees present (external impact)
 * - timeline_event with ahoy_object_deleted: Always (one-way door)
 * - timeline_event with ahoy_object_merged: Always (one-way door)
 */
export function isSignificantAction(action: ProposedAction): boolean {
  // Email - always significant (external impact)
  if (action.proposed_action_type_name === 'gmail_send') {
    return true;
  }

  // Calendar - only if has attendees (external impact)
  if (isCalendarEventCreatePayload(action.payload)) {
    const params = (action.payload as CalendarEventCreatePayload).action_params;
    return (params.attendees?.length ?? 0) > 0;
  }

  // Delete - always significant (one-way door)
  if (isObjectDeletedPayload(action.payload)) {
    return true;
  }

  // Merge - always significant (one-way door)
  if (isObjectMergedPayload(action.payload)) {
    return true;
  }

  return false;
}

/**
 * Get significant actions from a proposal
 *
 * @param proposal The proposal to check
 * @param selectedActionIds Optional array of selected action IDs. If provided, only returns
 *                          significant actions that are also in this list.
 */
export function getSignificantActions(
  proposal: ProposalDetail,
  selectedActionIds?: number[]
): ProposedAction[] {
  let actions = proposal.actions ?? [];

  // Filter by selection if provided
  if (selectedActionIds !== undefined) {
    actions = actions.filter(a => selectedActionIds.includes(a.id));
  }

  return actions.filter(isSignificantAction);
}

/**
 * Get the action type label for an action
 */
function getActionTypeLabel(action: ProposedAction): ActionTypeLabel | null {
  if (action.proposed_action_type_name === 'gmail_send') {
    return 'email';
  }
  if (isCalendarEventCreatePayload(action.payload)) {
    return 'calendar';
  }
  if (isObjectDeletedPayload(action.payload)) {
    return 'delete';
  }
  if (isObjectMergedPayload(action.payload)) {
    return 'merge';
  }
  return null;
}

/**
 * Get warning text for a single action
 *
 * Returns null for non-significant actions
 */
export function getActionWarningText(
  action: ProposedAction
): ActionWarningText | null {
  // Email action
  if (
    action.proposed_action_type_name === 'gmail_send' &&
    isGmailSendPayload(action.payload)
  ) {
    const payload = action.payload as GmailSendPayload;
    const toRecipients = payload.action_params.to;
    const ccRecipients = payload.action_params.cc ?? [];

    const recipientList = toRecipients.join(', ');
    const tooltipParts = [recipientList];
    if (ccRecipients.length > 0) {
      tooltipParts.push(`CC: ${ccRecipients.join(', ')}`);
    }

    return {
      condensed: `Email will be sent to ${recipientList}`,
      tooltip: tooltipParts.join('; '),
    };
  }

  // Calendar action with attendees
  if (isCalendarEventCreatePayload(action.payload)) {
    const payload = action.payload as CalendarEventCreatePayload;
    const attendees = payload.action_params.attendees ?? [];

    if (attendees.length === 0) {
      return null;
    }

    const count = attendees.length;
    const plural = count === 1 ? '' : 's';
    const singular = count === 1 ? '' : 's';

    return {
      condensed: `Calendar invite${singular} will be sent to ${count} attendee${plural}`,
      tooltip: `Attendees: ${attendees.join(', ')}`,
    };
  }

  // Delete action
  if (isObjectDeletedPayload(action.payload)) {
    const payload = action.payload as ObjectDeletedEntityPayload;
    const enrichedData = action.enriched_data;

    if (enrichedData?.object_label) {
      const objectType = getEntityTypeLabel(enrichedData.entity_type_name);
      return {
        condensed: `"${enrichedData.object_label}" will be permanently deleted`,
        tooltip: `${objectType} "${enrichedData.object_label}" will be permanently deleted`,
      };
    }

    // Fallback to entity_id
    return {
      condensed: `Object ${payload.entity_id} will be permanently deleted`,
      tooltip: `Object ${payload.entity_id} will be permanently deleted`,
    };
  }

  // Merge action
  if (isObjectMergedPayload(action.payload)) {
    const payload = action.payload as ObjectMergedEntityPayload;
    const enrichedData = action.enriched_data;

    // The deleted source is enriched as source_object; the survivor is the
    // top-level object_label / entity_id.
    if (enrichedData?.source_object && enrichedData?.object_label) {
      const sourceLabel = enrichedData.source_object.label;
      const targetLabel = enrichedData.object_label;
      const objectType = getEntityTypeLabel(enrichedData.entity_type_name);

      return {
        condensed: `"${sourceLabel}" will be merged into "${targetLabel}"`,
        tooltip: `${objectType} "${sourceLabel}" will be merged into "${targetLabel}"`,
      };
    }

    // Fallback to entity_ids
    const sourceId = payload.payload.source_entity_id;
    return {
      condensed: `Object ${sourceId} will be merged into ${payload.entity_id}`,
      tooltip: `Object ${sourceId} will be merged into ${payload.entity_id}`,
    };
  }

  // Non-significant action
  return null;
}

/**
 * Get multi-action summary for a proposal
 *
 * Returns null if 0 or 1 significant actions
 *
 * @param proposal The proposal to check
 * @param selectedActionIds Optional array of selected action IDs. If provided, only considers
 *                          significant actions that are also in this list.
 */
export function getMultiActionSummary(
  proposal: ProposalDetail,
  selectedActionIds?: number[]
): MultiActionSummary | null {
  const significantActions = getSignificantActions(proposal, selectedActionIds);

  if (significantActions.length <= 1) {
    return null;
  }

  // Get unique action type labels
  const typeLabels = significantActions
    .map(getActionTypeLabel)
    .filter((label): label is ActionTypeLabel => label !== null);

  const uniqueLabels = [...new Set(typeLabels)];

  // Get tooltip items for each significant action
  const tooltipItems = significantActions
    .map(action => {
      const warning = getActionWarningText(action);
      return warning?.condensed ?? null;
    })
    .filter((item): item is string => item !== null);

  return {
    summary: `${significantActions.length} actions: ${uniqueLabels.join(', ')}`,
    tooltipItems,
  };
}

/**
 * Get already-executed badge text for an action
 *
 * Returns type-specific text indicating the action already ran successfully
 * and will not re-run on re-approval.
 */
export function getAlreadyExecutedBadgeText(action: ProposedAction): string {
  const typeName = action.proposed_action_type_name;

  if (typeName === 'gmail_send') {
    return 'Already sent';
  }
  if (typeName === 'calendar_event_create') {
    return 'Already created';
  }
  if (typeName === 'timeline_event') {
    if (isObjectDeletedPayload(action.payload)) {
      return 'Already deleted';
    }
    if (isObjectMergedPayload(action.payload)) {
      return 'Already merged';
    }
    return 'Already saved';
  }
  return 'Already ran';
}

/**
 * Get skipped badge text for an action
 *
 * Returns type-specific text indicating the action won't be applied
 */
export function getSkippedBadgeText(action: ProposedAction): string {
  const typeName = action.proposed_action_type_name;

  if (typeName === 'gmail_send') {
    return "Won't send";
  }
  if (typeName === 'calendar_event_create') {
    return "Won't create";
  }
  if (typeName === 'external_action') {
    return "Won't run";
  }
  if (typeName === 'timeline_event') {
    // Check payload type for CRM operations
    if (isObjectDeletedPayload(action.payload)) {
      return "Won't delete";
    }
    if (isObjectMergedPayload(action.payload)) {
      return "Won't merge";
    }
    // Default for timeline events (creates, updates)
    return "Won't save";
  }
  // Fallback
  return "Won't apply";
}

/**
 * Derive entity type name from a non-email action for label grouping.
 * Returns the ahoy_* entity type name, or undefined if not determinable.
 */
function getActionEntityType(action: ProposedAction): string | undefined {
  if (isPropertyUpdatePayload(action.payload)) {
    return (action.payload as PropertyUpdateEntityPayload).entity_type_name;
  }
  const payload = action.payload as { entity_type_name?: string };
  return payload.entity_type_name;
}

/**
 * Extract event_type_name from a timeline event action's payload.
 * Returns undefined for non-timeline actions (email, calendar, external).
 */
function getActionEventType(action: ProposedAction): string | undefined {
  const payload = action.payload as { event_type_name?: string };
  return payload.event_type_name;
}

/**
 * Get contextual approve button label based on selected actions.
 *
 * Returns a dynamic label or null to fall back to default StickyFooter logic.
 * Label varies by action composition:
 * - Single non-email action → registry's contextual label ("Create contact", "Delete deal")
 * - Emails: "Send email" / "Send N emails"
 * - Same entity + same event type: "Create 6 contacts", "Delete 2 deals"
 * - Same entity + mixed events: "Update contact"
 * - Mixed entities: "Approve N updates"
 * - Email + CRM combo: "Send email & update contact"
 *
 * When mergedItems is provided, merged create+association groups count as 1 visual item.
 */
export function getApproveButtonLabel(
  actions: ProposedAction[],
  selectedActionIds?: number[],
  mergedItems?: MergedOrOriginal[],
  currentUserEmail?: string
): string | null {
  // If no selection tracking or all selected, return null (use existing logic)
  if (selectedActionIds === undefined) {
    return null;
  }

  const selectedSet = new Set(selectedActionIds);

  // Filter to selected actions only
  const selectedActions = actions.filter(a => selectedSet.has(a.id));

  // If nothing selected, return null (button should be disabled)
  if (selectedActions.length === 0) {
    return null;
  }

  // Count emails — always 1 per email action regardless of merging.
  const emailActions = selectedActions.filter(
    a => a.proposed_action_type_name === 'gmail_send'
  );
  const emailCount = emailActions.length;

  // Count "other" visual items (non-email CRM actions).
  // When mergedItems is provided, a merged group (create + associations) counts as 1
  // if the primary create action is selected. Absorbed association actions don't add to count.
  const otherActions: ProposedAction[] = [];
  if (mergedItems) {
    const items = mergedItems.length > 0 ? mergedItems : mergeActions(actions);
    for (const item of items) {
      if (item.type === 'merged') {
        if (selectedSet.has(item.merged.primaryAction.id)) {
          otherActions.push(item.merged.primaryAction);
        }
      } else if (
        selectedSet.has(item.action.id) &&
        item.action.proposed_action_type_name !== 'gmail_send'
      ) {
        otherActions.push(item.action);
      }
    }
  } else {
    for (const a of selectedActions) {
      if (a.proposed_action_type_name !== 'gmail_send') {
        otherActions.push(a);
      }
    }
  }
  const otherCount = otherActions.length;

  // For a single non-email action, use the registry's contextual approve label
  if (otherCount === 1 && emailCount === 0) {
    const entry = lookupAction(otherActions[0]);
    if (entry?.getApproveLabel) {
      return entry.getApproveLabel(otherActions[0].payload, otherActions[0], {
        currentUserEmail,
      });
    }
  }

  // Build the CRM part of the label.
  // Uses verb-specific labels when all actions share entity type + event type:
  //   - All creates → "create 6 contacts"
  //   - All deletes → "delete 3 deals"
  //   - All updates (or mixed event types) → "update contact"
  //   - Mixed entity types or missing entity types → "approve N updates"
  let emailPart = '';
  if (emailCount === 1) {
    emailPart = 'Send email';
  } else if (emailCount > 1) {
    emailPart = `Send ${emailCount} emails`;
  }

  let crmPart = '';
  if (otherCount > 0) {
    const rawEntityTypes = otherActions.map(a => getActionEntityType(a));
    const allHaveEntityType = rawEntityTypes.every(Boolean);

    if (allHaveEntityType && new Set(rawEntityTypes).size === 1) {
      const entityTypeName = rawEntityTypes[0] as string;
      const entityType = deriveEntityType(entityTypeName);
      const singular = getEntityTypeLabel(entityType).toLowerCase();

      // Check if all actions share the same event type for verb-specific labels
      const rawEventTypes = otherActions.map(a => getActionEventType(a));
      const allHaveEventType = rawEventTypes.every(Boolean);
      const uniqueEventType =
        allHaveEventType && new Set(rawEventTypes).size === 1
          ? rawEventTypes[0]
          : undefined;

      if (uniqueEventType === 'ahoy_object_created') {
        const plural = pluralizeEntityLabel(singular);
        crmPart =
          otherCount === 1
            ? `create ${singular}`
            : `create ${otherCount} ${plural}`;
      } else if (uniqueEventType === 'ahoy_object_deleted') {
        const plural = pluralizeEntityLabel(singular);
        crmPart =
          otherCount === 1
            ? `delete ${singular}`
            : `delete ${otherCount} ${plural}`;
      } else if (uniqueEventType === 'ahoy_object_merged') {
        // Always "merge {plural}" — no count, since each merge already involves 2 entities
        const plural = pluralizeEntityLabel(singular);
        crmPart = `merge ${plural}`;
      } else if (uniqueEventType) {
        // All actions share one non-create/delete/merge event type (property
        // or stage updates). Unlike creates/deletes, several update actions
        // can target the SAME record (multiple property edits), so the count
        // is of DISTINCT target records, not actions: one record →
        // "update task"; three records → "update 3 tasks". When any action
        // doesn't name its record, a count would lie — keep the count-less
        // singular.
        const entityIds = otherActions.map(
          a => (a.payload as { entity_id?: string }).entity_id
        );
        const distinctEntityCount = entityIds.every(Boolean)
          ? new Set(entityIds).size
          : 1;
        crmPart =
          distinctEntityCount > 1
            ? `update ${distinctEntityCount} ${pluralizeEntityLabel(singular)}`
            : `update ${singular}`;
      } else {
        // Genuinely mixed event types (e.g. some creates + some updates) — no
        // single verb fits, so use the neutral count label rather than a
        // misleading "update".
        crmPart = `approve ${otherCount} actions`;
      }
    } else {
      // Mixed entity types or actions without entity type (calendar, external)
      crmPart = `approve ${otherCount} actions`;
    }
  }

  // Combine parts
  if (emailPart && crmPart) {
    return `${emailPart} & ${crmPart}`;
  }
  if (emailPart) {
    return emailPart;
  }
  if (crmPart) {
    // Capitalize first letter
    return crmPart.charAt(0).toUpperCase() + crmPart.slice(1);
  }

  return null;
}
